import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Meteor } from 'meteor/meteor';
import { useFind, useUser } from '/imports/hooks';
import { defer, firstBy, formatDateTime, randomString } from '/imports/utils';
import { strings } from '/imports/strings';
import { toastOnError } from '/imports/ui/utils';
import { Icon } from '/imports/ui/Icon';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { TextArea } from '/imports/ui/TextArea';
import { Publications } from '/imports/publications';
import { Conversation, modelClass as ConversationClass, getConversationForOrg } from '/imports/api/conversations';
import { Message, Messages, markConversationMessagesRead, modelClass as messageModelClass } from '/imports/api/messages';

// Fixed bottom drawer (desktop: right side panel). `transition` toggles the
// open state; the bottom transition animates because the element stays mounted.
// vbounce keyframe lives in client/tailwind.css.
const conversationFeedClass = [
  'conversation-feed fixed bottom-[-100%] left-0 box-border flex h-[calc(100%-8.25rem)] w-full flex-col border-t-2 border-mid bg-panel transition-[bottom] duration-500 ease-[ease]',
  'desktop:left-auto desktop:right-0 desktop:h-[50rem] desktop:w-[35rem] desktop:border-l-2',
  '[&.transition]:bottom-[3.75rem] desktop:[&.transition]:bottom-0 desktop:[&.transition]:h-[calc(100%-8.25rem+4.75rem)]',
].join(' ');

type ConversationFeedProps = {
  conversationId?: string;
  orgId?: string;
  className?: string;
  onClose?: () => void;
};

export const ConversationFeed = (props: ConversationFeedProps = {}) => {
  const { conversationId, orgId, className, onClose } = props;
  const user = useUser();
  const userId = user?._id;
  const [loadingConversation, setLoadingConversation] = useState(true);
  const loading = loadingConversation && !user; // || loadingMessages;
  const [textareaId] = useState(`t${randomString()}`);
  const [messageDivId] = useState(`m${randomString()}`);
  const [conversation, setConversation] = useState<Conversation>();
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  useEffect(() => { loadConversation(); }, []);
  const loadConversation = toastOnError(async () => {
    if (conversationId) setConversation(await ConversationClass.getMethod.call(conversationId));
    else if (orgId) setConversation(await getConversationForOrg.call(orgId));
    setLoadingConversation(false);
  });
  const [messageSubscription, setMessageSubscription] = useState<Meteor.SubscriptionHandle>();
  useEffect(() => {
    markAsRead();
    subscribeToMessages();
  }, [conversation?._id]);
  const subscribeToMessages = () => {
    const conversationId = conversation?._id;
    const messageSubscription = conversationId && Meteor.subscribe(Publications.conversationMessages, { conversationId, limit }) || undefined;
    messageSubscription && setMessageSubscription(messageSubscription);
    const interval = setInterval(() => {
      if (messageSubscription?.ready()) {
        setMessagesLoaded(true);
        clearInterval(interval);
      }
    }, 100);
  };
  const markAsRead = async () => {
    conversationId && await markConversationMessagesRead.call(conversationId);
  };
  const [limit, setLimit] = useState(10);
  useEffect(() => {
    subscribeToMessages();
  }, [limit]);
  const handleScrollMessages = () => {
    const messageDiv = document.getElementById(messageDivId) as HTMLDivElement;
    if (!messageDiv) return;
    if (messageDiv.scrollTop === 0) {
      const topMessage = messages[0];
      const topMessageDiv = document.getElementById(`b${topMessage?._id}`);
      setLimit(limit + 10);
      let iters = 0;
      let lastScrollHeight = messageDiv.scrollHeight;
      const interval = setInterval(() => {
        topMessageDiv?.scrollIntoView();
        const scrollHeight = messageDiv.scrollHeight;
        if (iters > 50 && lastScrollHeight === scrollHeight || iters > 1000) clearInterval(interval);
        lastScrollHeight = scrollHeight;
        iters += 1;
      }, 1);
    }
  };
  const [content, setContent] = useState<string>();
  const [transition, setTransition] = useState(false);
  useEffect(() => { setTransition(true); }, []);
  const close = () => {
    setTransition(false);
    setTimeout(() => onClose?.(), 500);
  };
  const [sending, setSending] = useState(false);
  const sendMessage = toastOnError(async () => {
    setSending(true);
    const conversationId = conversation?._id;
    conversationId && await messageModelClass.createMethod.call({ conversationId, content });
    clearContent();
    setSending(false);
  });
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.nativeEvent.preventDefault();
      sendMessage();
    }
  }, [content]);
  const clearContent = () => {
    setContent(undefined);
    const textarea = document.getElementById(textareaId) as HTMLTextAreaElement;
    if (textarea) textarea.value = '';
  };
  const messages = useFind(() => Messages.find({ conversationId: conversation?._id }), [conversation?._id, messageSubscription?.ready()]);
  const sortedMessages = useMemo(() => messages.sort(firstBy('createdAt', { direction: 'asc' })), [messages]);
  const mostRecentMessage = useRef(sortedMessages[sortedMessages.length - 1]);
  const [showNewMessagesBubble, setShowNewMessagesBubble] = useState(false);
  const [initialScroll, setInitialScroll] = useState(true);
  useEffect(() => {
    const _mostRecentMessage = sortedMessages[sortedMessages.length - 1];
    const _createdAt = _mostRecentMessage?.createdAt?.getTime() || 1;
    const createdAt = mostRecentMessage.current?.createdAt?.getTime() || 0;
    if (_createdAt > createdAt) {
      mostRecentMessage.current = _mostRecentMessage;
      scrollToBottom();
    }
    markAsRead();
  }, [sortedMessages]);
  useEffect(() => {
    messagesLoaded && defer(() => scrollToBottom(true, 'instant'));
  }, [messagesLoaded]);
  const scrollToBottom = (force = false, behavior: ScrollBehavior = 'smooth') => {
    const messageDiv = document.getElementById(messageDivId) as HTMLDivElement;
    if (!messageDiv) return;
    force = force || initialScroll || !messageSubscription?.ready();
    if (force || messageDiv.scrollHeight - messageDiv.clientHeight - messageDiv.scrollTop <= 133) {
      setShowNewMessagesBubble(false);
      setInitialScroll(false);
      messageDiv?.scrollTo({ top: messageDiv.scrollHeight, behavior });
    }
    else {
      setShowNewMessagesBubble(true);
    }
  };
  return (
    <div className={`${conversationFeedClass}${transition ? ' transition' : ''}${className || ''}`}>
      {
        loading ?
          <LoadingFill />
          :
          null
      }
      {
        conversation ?
          <>
            <div className="header px-0 pt-8 pb-4 text-center">
              <div className="title font-bold">{conversation.name}</div>
            </div>
            <div id={messageDivId} className="messages h-full overflow-auto" onScroll={() => handleScrollMessages()}>
              {
                sortedMessages.map((m) => {
                  return (
                    <MessageBubble id={`b${m._id}`} key={m._id} message={m} isSender={m.senderUserId === userId} />
                  );
                })
              }
            </div>
            <div className="form relative box-border w-full p-4 [&_textarea]:h-input [&_textarea]:w-full">
              <TextArea id={textareaId} value={content} onKeyDown={(e) => handleKeyDown(e)} onChange={(message) => setContent(message)} />
              <SendButton sending={sending} onSend={() => sendMessage()} />
              {
                showNewMessagesBubble ?
                  <div className="new-messages absolute left-0 top-[-0.5rem] w-full text-center">
                    <span
                      className="inline-block cursor-pointer animate-[vbounce_0.5s_alternate_infinite] rounded-input bg-primary px-2 py-1 text-[0.8rem] text-on-primary opacity-80"
                      onClick={() => scrollToBottom(true)}
                    >
                      {strings.NewMessages()} ⮟
                    </span>
                  </div>
                  :
                  null
              }
            </div>
          </>
          :
          null
      }
      <Icon className="close absolute top-2 right-2 z-[111] cursor-pointer" icon="close" onClick={() => close()} />
    </div>
  );
};

interface MessageBubbleProps {
  id: string;
  message: Message;
  isSender: boolean;
}

// Sent (right, sky-blue) vs received (left, grey) is driven by the wrapper's
// .sent/.received class; per-state child overrides via [&.sent_.x]/[&.received_.x].
const messageBubbleClass = [
  'message mb-4 box-border p-4',
  '[&.sent_.sender]:mr-4 [&.sent_.sender]:text-right [&.sent_.content]:rounded-tr-none [&.sent_.content]:bg-sky-blue [&.sent_.content]:text-white [&.sent_.timestamp]:mr-4 [&.sent_.timestamp]:text-right',
  '[&.received_.sender]:ml-4 [&.received_.content]:rounded-tl-none [&.received_.content]:bg-grey-7 [&.received_.timestamp]:ml-4',
].join(' ');

const MessageBubble = (props: MessageBubbleProps) => {
  const { id, message, isSender } = props;
  return (
    <div id={id} className={`${messageBubbleClass} ${isSender ? 'sent' : 'received'}`}>
      <div className="sender text-xs text-grey-4">
        {isSender && strings.You() || message.senderOrgName}
      </div>
      <div className="content rounded-2xl p-3 whitespace-pre-wrap">
        {message.content}
      </div>
      <div className="timestamp text-xs text-grey-4">
        {message.createdAt && formatDateTime(message.createdAt) || null}
      </div>
    </div>
  );
};

const sendButtonClass = 'absolute top-6 right-8 cursor-pointer transition-[top] duration-[0.25s] ease-[ease] hover:top-5 [&_svg]:w-6 [&_svg]:transition-[width] [&_svg]:duration-[0.25s] [&_svg]:ease-[ease] [&:hover_svg]:w-7 [&.sending]:text-grey-5';

interface SendButtonProps {
  sending?: boolean;
  onSend?: () => void;
}

const SendButton = (props: SendButtonProps) => {
  const sending = props.sending;
  const svg = `
  <svg id="Layer_2" data-name="Layer 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 211.86 292.61">
    <defs>
      <style>
        .cls-1 {
          fill: #f4974c;
        }

        .cls-1, .cls-2, .cls-3 {
          stroke-width: 0px;
        }

        .cls-2 {
          fill: #15a1e2;
        }

        .cls-3 {
          fill: #81dd74;
        }
      </style>
    </defs>
    <g id="send-button-g" data-name="send-button-g">
      <g>
        <path class="cls-1" d="M105.94,66.88v52.78c0,5.85,3.12,11.25,8.18,14.18l83.56,48.25c6.3,3.64,14.17-.91,14.17-8.18v-52.78c0-5.85-3.12-11.25-8.18-14.18l-83.56-48.25c-6.3-3.64-14.17.91-14.17,8.18Z"/>
        <path class="cls-2" d="M52.06,264.04l45.7,26.38c5.06,2.92,11.3,2.92,16.37,0l83.56-48.25c6.3-3.64,6.3-12.73,0-16.36l-45.7-26.38c-5.06-2.92-11.3-2.92-16.37,0l-83.56,48.25c-6.3,3.64-6.3,12.73,0,16.36Z"/>
        <path class="cls-3" d="M0,41.84v175.79c0,7.27,7.87,11.82,14.17,8.18l45.7-26.38c5.06-2.92,8.19-8.33,8.19-14.18V9.46c0-7.27-7.87-11.82-14.17-8.18L8.19,27.66C3.12,30.58,0,35.99,0,41.84Z"/>
      </g>
    </g>
  </svg>`;
  return (
    <span onClick={() => props.onSend?.()} className={`${sendButtonClass}${sending ? ' sending' : ''}`} dangerouslySetInnerHTML={{ __html: svg }} />
  );
};